/**
 * Merging a block with another of the same client's that day, or with one
 * without a client, which takes it (ADRs 0062 and 0118); and splitting it in
 * two between its activities (section 9.4; ADR 0062). The popup's More row
 * opens each in its right pane (ADR 0193): Merge is how a keyboard merges, as
 * a mouse can also drag one card onto another (useDragToMerge).
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";

import { ApiError } from "../../api/client";
import { useSession } from "../../app/session-context";
import { formatClockIn, formatClockRange } from "../../lib/dates";
import { type BlockView, mergeCheck, mergeable, splitPoints, takesNote } from "./model";

const secondary =
  "h-10 cursor-pointer self-start rounded-pill border border-line bg-panel px-4 text-[13.5px] font-semibold hover:bg-panel-2 disabled:cursor-not-allowed disabled:opacity-50";
const primary =
  "h-10 cursor-pointer rounded-pill bg-cta-bg px-5 text-[13.5px] font-bold text-cta-fg disabled:cursor-not-allowed disabled:opacity-50";

function refusal(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 403) {
      return "You can only change your own blocks.";
    }
    if (error.status === 409 && error.detail) {
      return `Couldn't do that: ${error.detail}.`;
    }
    return error.detail ?? error.message;
  }
  return error instanceof Error ? error.message : "Something went wrong.";
}

function Edit<T>({
  formLabel,
  selectLabel,
  options,
  action,
  run,
  said,
  onDone,
  onCancel,
  done,
  note,
}: {
  formLabel: string;
  selectLabel: string;
  options: { value: string; label: string }[];
  action: string;
  run: (value: string) => Promise<T>;
  /** What happened, in words (#247). */
  said: string;
  onDone: (message: string) => void;
  /** Cancel: back to what the popup showed before (ADR 0193). */
  onCancel: () => void;
  /** Called with the answer, before the day is asked for again (#248). */
  done?: ((result: T) => void) | undefined;
  /** What the chosen option will do, if it needs saying. */
  note?: (value: string) => string | null;
}) {
  const queryClient = useQueryClient();
  const selectId = useId();
  const noteId = useId();
  const [value, setValue] = useState("");
  const noteText = value !== "" && note ? note(value) : null;
  const mutation = useMutation({
    mutationFn: run,
    onSuccess: async (result) => {
      done?.(result);
      // Closed once the day shows it, with focus moved off the form (#247).
      await queryClient.invalidateQueries({ queryKey: ["day"] });
      onDone(said);
      // The Log form's preview (title, notes, hours) is built from the block's
      // activities: ask for it again, or it shows the old block (#224).
      await queryClient.invalidateQueries({ queryKey: ["submission-preview"] });
    },
  });

  return (
    <form
      aria-label={formLabel}
      className="flex flex-col gap-2.5"
      onSubmit={(event) => {
        event.preventDefault();
        if (value !== "") {
          mutation.mutate(value);
        }
      }}
    >
      <label htmlFor={selectId} className="text-xs font-semibold text-muted">
        {selectLabel}
      </label>
      <select
        id={selectId}
        aria-describedby={noteText ? noteId : undefined}
        value={value}
        onChange={(event) => {
          setValue(event.target.value);
        }}
        className="h-10 w-full rounded-input border border-line bg-card px-2 text-[13.5px] text-text"
      >
        <option value="">Choose</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {noteText ? (
        <p id={noteId} className="text-[13px] text-muted">
          {noteText}
        </p>
      ) : null}
      {mutation.isError ? (
        <p role="alert" className="text-[13px] text-status-needs-client-fg">
          {refusal(mutation.error)}
        </p>
      ) : null}
      <div className="flex gap-2">
        <button type="submit" disabled={value === "" || mutation.isPending} className={primary}>
          {mutation.isPending ? "Working…" : action}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className={secondary}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export function MergeBlock({
  block,
  targets,
  timeZone,
  onDone,
  onCancel,
  onSelect,
}: {
  block: BlockView;
  targets: readonly BlockView[];
  timeZone: string;
  /** Told what happened, once the day shows it (#247). */
  onDone: (message: string) => void;
  /** Cancel: back to what the popup showed before (ADR 0193). */
  onCancel: () => void;
  /** Shows a block in the panel: the kept one, when this one is merged away (#248). */
  onSelect?: ((blockId: string) => void) | undefined;
}) {
  const { api } = useSession();
  const candidates = mergeable(block, targets);
  if (candidates.length === 0) {
    return null;
  }
  return (
    <Edit
      formLabel="Merge with another block"
      selectLabel="Merge with"
      options={candidates.map((t) => ({
        value: t.id,
        label: `${t.title} · ${formatClockRange(t.startedAt, t.endedAt, timeZone)} · ${t.duration}`,
      }))}
      action="Merge"
      run={(other) => api.mergeBlocks(block.id, other)}
      said="Merged into one block."
      onDone={onDone}
      onCancel={onCancel}
      done={(merged) => {
        if (merged.removed_block_id === block.id) {
          onSelect?.(merged.block_id);
        }
      }}
      note={(other) => {
        const target = candidates.find((t) => t.id === other);
        const check = target ? mergeCheck(block, target) : null;
        return check?.merges && check.takes ? takesNote(check.takes) : null;
      }}
    />
  );
}

export function SplitBlock({
  block,
  timeZone,
  onDone,
  onCancel,
}: {
  block: BlockView;
  timeZone: string;
  /** Told what happened, once the day shows it (#247). */
  onDone: (message: string) => void;
  /** Cancel: back to what the popup showed before (ADR 0193). */
  onCancel: () => void;
}) {
  const { api } = useSession();
  const points = splitPoints(block, timeZone);
  if (points.length === 0) {
    return null;
  }
  return (
    <Edit
      formLabel="Split this block"
      selectLabel="Split at"
      options={points.map((p) => ({
        value: p.at,
        label: `${formatClockIn(p.at, timeZone)} (${String(p.before)} before, ${String(p.after)} from then)`,
      }))}
      action="Split"
      run={(at) => api.splitBlock(block.id, at)}
      said="Split into two blocks."
      onDone={onDone}
      onCancel={onCancel}
    />
  );
}
